import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useUserRole } from "@/hooks/useUserRole";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar, Plus, Edit, Trash2, Filter } from "lucide-react";
import { toast } from "sonner";
import { format, startOfDay, endOfDay, isSameDay, endOfWeek, endOfMonth } from "date-fns";
import CalendarEventDialog from "./CalendarEventDialog";
import { useUrlState } from "@/lib/urlState";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface CalendarEvent {
  id: string;
  student_user_id: string;
  creator_id: string;
  creator_type: 'student' | 'parent' | 'tutor';
  title: string;
  description: string | null;
  start_time: string;
  end_time: string;
  color: string;
  created_at: string;
  series_id: string | null;
}

interface StudentCalendarViewProps {
  studentUserId: string;
  studentName?: string;
}

export default function StudentCalendarView({ studentUserId, studentName }: StudentCalendarViewProps) {
  const { user } = useAuth();
  const { role } = useUserRole();
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [eventToDelete, setEventToDelete] = useState<string | null>(null);
  const [deleteRecurringDialog, setDeleteRecurringDialog] = useState(false);
  const [eventToDeleteRecurring, setEventToDeleteRecurring] = useState<CalendarEvent | null>(null);
  // Quick-switch date range, synced to the URL via `?cal=today|week|month|all`.
  // Defaults to "today" so dashboards open focused on what's happening now.
  const url = useUrlState();
  type DateFilter = 'today' | 'week' | 'month' | 'all';
  const FILTERS: { value: DateFilter; label: string }[] = [
    { value: 'today', label: 'Today' },
    { value: 'week', label: 'Week' },
    { value: 'month', label: 'Month' },
    { value: 'all', label: 'All' },
  ];
  const rawFilter = url.get('cal') as DateFilter | null;
  const dateFilter: DateFilter =
    rawFilter && FILTERS.some(f => f.value === rawFilter) ? rawFilter : 'today';
  const setDateFilter = (value: DateFilter) =>
    url.set({ cal: value === 'today' ? null : value });

  useEffect(() => {
    loadEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentUserId, dateFilter]);

  const loadEvents = async () => {
    try {
      let query = supabase
        .from('calendar_events')
        .select('*')
        .eq('student_user_id', studentUserId)
        .is('deleted_at', null)
        .order('start_time', { ascending: true });

      // Apply date filter (rolling windows from "now").
      const today = new Date();
      if (dateFilter === 'today') {
        query = query
          .gte('start_time', startOfDay(today).toISOString())
          .lte('start_time', endOfDay(today).toISOString());
      } else if (dateFilter === 'week') {
        query = query
          .gte('start_time', startOfDay(today).toISOString())
          .lte('start_time', endOfDay(endOfWeek(today)).toISOString());
      } else if (dateFilter === 'month') {
        query = query
          .gte('start_time', startOfDay(today).toISOString())
          .lte('start_time', endOfDay(endOfMonth(today)).toISOString());
      }

      const { data, error } = await query;

      if (error) throw error;
      setEvents(data || []);
    } catch (error) {
      console.error('Error loading events:', error);
      toast.error('Failed to load calendar events');
    } finally {
      setLoading(false);
    }
  };

  const canEditEvent = (event: CalendarEvent) => {
    return user?.id === event.creator_id;
  };

  const shouldShowDetails = (event: CalendarEvent) => {
    // Tutors can only see details of events they created
    if (role === 'instructor') {
      return user?.id === event.creator_id;
    }
    // Students and parents can see all details
    return true;
  };

  const handleAddEvent = () => {
    setSelectedEvent(null);
    setDialogOpen(true);
  };

  const handleEditEvent = (event: CalendarEvent) => {
    setSelectedEvent(event);
    setDialogOpen(true);
  };

  // Soft-delete a single event (sets deleted_at; preserves history).
  const handleDeleteEvent = async (eventId: string) => {
    try {
      const { error } = await supabase
        .from('calendar_events')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', eventId);

      if (error) throw error;

      toast.success('Event deleted');
      loadEvents();
    } catch (error: any) {
      console.error('Error deleting event:', error);
      toast.error(error.message || 'Failed to delete event');
    } finally {
      setEventToDelete(null);
    }
  };

  // Soft-delete by series. Prefers series_id when available; falls back to
  // title+creator matching for legacy events created before series_id existed.
  const handleDeleteRecurring = async (deleteType: 'this' | 'future' | 'all') => {
    if (!eventToDeleteRecurring) return;
    const target = eventToDeleteRecurring;

    try {
      if (deleteType === 'this') {
        const { error } = await supabase
          .from('calendar_events')
          .update({ deleted_at: new Date().toISOString() })
          .eq('id', target.id);
        if (error) throw error;
        toast.success('Event deleted');
      } else {
        const stamp = new Date().toISOString();
        let query = supabase
          .from('calendar_events')
          .update({ deleted_at: stamp })
          .eq('student_user_id', studentUserId)
          .is('deleted_at', null);

        if (target.series_id) {
          query = query.eq('series_id', target.series_id);
        } else {
          // Legacy fallback: match by title + creator
          query = query
            .eq('title', target.title)
            .eq('creator_id', target.creator_id);
        }

        if (deleteType === 'future') {
          query = query.gte('start_time', target.start_time);
        }

        const { error } = await query;
        if (error) throw error;
        toast.success(
          deleteType === 'all'
            ? 'All events in series deleted'
            : 'This and future events deleted'
        );
      }

      loadEvents();
    } catch (error: any) {
      console.error('Error deleting recurring events:', error);
      toast.error(error.message || 'Failed to delete recurring events');
    } finally {
      setDeleteRecurringDialog(false);
      setEventToDeleteRecurring(null);
    }
  };

  const checkIfRecurring = (event: CalendarEvent): boolean => {
    if (event.series_id) {
      return events.some(e => e.series_id === event.series_id && e.id !== event.id);
    }
    // Legacy fallback for events created before series_id existed
    return events.some(e =>
      e.title === event.title &&
      e.creator_id === event.creator_id &&
      e.id !== event.id
    );
  };

  const handleDeleteClick = (event: CalendarEvent) => {
    if (checkIfRecurring(event)) {
      setEventToDeleteRecurring(event);
      setDeleteRecurringDialog(true);
    } else {
      setEventToDelete(event.id);
    }
  };

  const formatEventTime = (event: CalendarEvent) => {
    const start = new Date(event.start_time);
    const end = new Date(event.end_time);
    return `${format(start, 'MMM d, yyyy h:mm a')} - ${format(end, 'h:mm a')}`;
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center text-muted-foreground">Loading calendar...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              <CardTitle>Calendar Events</CardTitle>
            </div>
            <Button onClick={handleAddEvent} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Add Event
            </Button>
          </div>
          {studentName && (
            <CardDescription>Manage calendar events for {studentName}</CardDescription>
          )}
          <div className="flex items-center gap-3 pt-4 flex-wrap">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <div
              role="tablist"
              aria-label="Date range"
              className="inline-flex rounded-md border bg-muted/50 p-0.5"
            >
              {FILTERS.map((f) => {
                const active = dateFilter === f.value;
                return (
                  <button
                    key={f.value}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setDateFilter(f.value)}
                    className={
                      'px-3 py-1 text-sm rounded-sm transition-colors ' +
                      (active
                        ? 'bg-background text-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground')
                    }
                  >
                    {f.label}
                  </button>
                );
              })}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {events.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No events scheduled yet</p>
              <Button onClick={handleAddEvent} variant="outline" className="mt-4">
                <Plus className="h-4 w-4 mr-2" />
                Add Your First Event
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {events.map((event) => {
                const showDetails = shouldShowDetails(event);
                const canEdit = canEditEvent(event);

                return (
                  <div
                    key={event.id}
                    className="p-4 border rounded-lg"
                    style={{ borderLeftWidth: '4px', borderLeftColor: event.color }}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-medium">
                          {showDetails ? event.title : 'Busy'}
                        </h3>
                        {showDetails && event.description && (
                          <p className="text-sm text-muted-foreground mt-1">
                            {event.description}
                          </p>
                        )}
                        <p className="text-sm text-muted-foreground mt-2">
                          {formatEventTime(event)}
                        </p>
                        {showDetails && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Created by: {event.creator_type === 'tutor' ? 'Tutor' : event.creator_type === 'parent' ? 'Parent' : 'Student'}
                          </p>
                        )}
                      </div>
                      {canEdit && (
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditEvent(event)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteClick(event)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <CalendarEventDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        event={selectedEvent}
        studentUserId={studentUserId}
        onEventSaved={loadEvents}
      />

      <AlertDialog open={!!eventToDelete} onOpenChange={() => setEventToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Event</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this event? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => eventToDelete && handleDeleteEvent(eventToDelete)}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteRecurringDialog} onOpenChange={setDeleteRecurringDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Recurring Events</AlertDialogTitle>
            <AlertDialogDescription>
              This event appears to be part of a recurring series. How would you like to delete it?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button variant="outline" onClick={() => handleDeleteRecurring('this')}>
              This Event Only
            </Button>
            <Button variant="outline" onClick={() => handleDeleteRecurring('future')}>
              This & Future Events
            </Button>
            <AlertDialogAction onClick={() => handleDeleteRecurring('all')}>
              All Events in Series
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
